import axios from 'axios';
/**
 * Class which communicates with backend using REST API and OAuth2
 * Api is able to deal with race condition between calls when refreshing _accessToken
 */

const delay = (delayTime) => new Promise((resolve) => {
  setTimeout(resolve, delayTime);
});

export class Api {
  static instance;

  axios;

  refreshingProcess = false;

  constructor() {
    // eslint-disable-next-line no-undef
    const axiosConfig = {
      baseURL: `${ API_URL }${ API_URL_PREFIX ?? '' }`,
      withCredentials: true,
    };

    this.axios = axios.create(axiosConfig);

    // Add error response interceptor
    this.axios.interceptors.response.use(undefined, this.retryRequest);
  }

  /**
   * Get instance of Api (singleton)
   * @return {Api}
   */
  static getInstance() {
    if (Api.instance === undefined) {
      Api.instance = new Api();
    }

    return Api.instance;
  }

  static redirectToLogin() {
    const {
      location: {
        href,
        origin,
        pathname,
      },
    } = window;

    if (`${ origin }${ pathname }` !== `${ GIDS_URL }/login`) {
      window.location = `${ GIDS_URL }/login?referer=${ href }`;
    }
  }

  /**
   * Set OAuth secret
   * @param secret
   */

  /**
   * Request with access token in headers
   * @public
   * @param {object} config
   * @return {Promise<object>}
   */
  async request(config) {
    const newConfig = { ...config };
    newConfig.headers = {
      'Content-Type': 'application/json',
      ...newConfig.headers,
    };

    try {
      return await this.axios(newConfig);
    } catch (e) {
      if (e?.response?.status === 401) {
        // GO TO LOGIN
      }

      throw e;
    }
  }

  static noAuthRequest(url, method = 'get', data = {}, params = {}) {
    const api = this.getInstance();

    const headers = {
      'Content-type': 'application/json',
    };

    return api.axios({
      url,
      baseURL: API_URL,
      method,
      headers,
      data,
      ...params,
    });
  }

  async retryRequest(err) {
    const {
      config,
      response,
    } = err;
    const api = Api.getInstance();

    if (response?.status === 404 && config.url && /\/oauth\/refresh$/.test(config.url) && response?.data?.code === 'CURRENT_USER_NOT_FOUND_EXCEPTION') {
      Api.redirectToLogin();
      return err;
    }

    if (response?.status === 401 && config.url && /\/oauth\/refresh$/.test(config.url)) {
      Api.redirectToLogin();
      return err;
    }

    // Session is expired
    if (response && response.status === 401 && response.data.error === 'invalid_token') {
      if (!api.refreshingProcess) {
        api.refreshingProcess = true;
        const refreshResponse = await api.axios({
          url: '/oauth/refresh',
          method: 'post',
          baseURL: GIDS_API,
        });

        api.refreshingProcess = false;

        if (refreshResponse.status === 200) {
          return api.request(config);
        }

        return err;
      } else {
        do {
          await delay(200);
        } while (!api.refreshingProcess);

        if (!sessionStorage.getItem('logged')) {
          Promise.reject(err);
        } else {
          return api.request(config);
        }
      }
    }

    return Promise.reject(err);
  }

  /**
   * Alias for request with method GET
   * @public
   * @param {string} url
   * @param {object} data
   * @param {object} headers
   * @return {Promise<object>}
   */
  static get(url, data = null, headers = {}, restPrams) {
    return Api.getInstance()
      .request({
        method: 'GET',
        url,
        data,
        headers,
        ...restPrams,
      });
  }

  /**
   * Alias for request with method POST
   * @public
   * @param {string} url
   * @param {object} data
   * @param {object} headers
   * @return {Promise<object>}
   */
  static post(url, data = null, headers = {}, restPrams) {
    return Api.getInstance()
      .request({
        method: 'POST',
        url,
        data,
        headers,
        ...restPrams,
      });
  }

  /**
   * Alias for request with method POST
   * @public
   * @param {string} url
   * @param {FormData} data
   * @param {function} onUploadProgress
   * @param {object} restPrams
   * @return {Promise<object>}
   */
  static upload(url, data = null, onUploadProgress = null, restPrams) {
    if (data instanceof FormData) {
      const config = {
        method: 'POST',
        url,
        data,
        onUploadProgress,
        ...restPrams,
      };

      if (onUploadProgress) {
        config.onUploadProgress = onUploadProgress;
      }

      return Api.getInstance()
        .request(config);
    }

    return Promise.reject(new Error('Upload data must be of type FormData'));
  }

  /**
   * Alias for request with method PUT
   * @public
   * @param {string} url
   * @param {object} data
   * @param {object} headers
   * @return {Promise<object>}
   */
  static put(url, data = null, headers = {}, restPrams) {
    return Api.getInstance()
      .request({
        method: 'PUT',
        url,
        data,
        headers,
        ...restPrams,
      });
  }

  /**
   * Alias for request with method PATCH
   * @public
   * @param {string} url
   * @param {object} data
   * @param {object} headers
   * @return {Promise<object>}
   */
  static patch(url, data = null, headers = {}, restPrams) {
    return Api.getInstance()
      .request({
        method: 'PATCH',
        url,
        data,
        headers,
        ...restPrams,
      });
  }

  /**
   * Alias for request with method DELETE
   * @public
   * @param {string} url
   * @param {object} data
   * @param {object} headers
   * @return {Promise<object>}
   */
  static delete(url, data = null, headers = {}, restPrams) {
    return Api.getInstance()
      .request({
        method: 'DELETE',
        url,
        data,
        headers,
        ...restPrams,
      });
  }

  static download(url) {
    return Api.getInstance()
      .request({
        url,
        responseType: 'blob',
      });
  }
}
